import {
  BulkMemberTransferRequestFormType,
  FilterOptionDto,
  MemberTransferReasonDto,
} from '@act/shared/data-transfer-objects';
import { SelectOption } from '@act/storybook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';

interface BulkMemberTransferRequestFormConfigParams {
  originatingGuideOptions: SelectOption<string>[];
  caseloadMemberOptions: FilterOptionDto[];
  receivingGuideOptions: SelectOption<string>[];
  memberTransferReasons: MemberTransferReasonDto[];
  selectedTransferReason: MemberTransferReasonDto | null;
  selectedMemberIds: string[];
  addressOptionsByMemberId: Record<
    string,
    { memberName: string; options: SelectOption<number>[] }
  >;
}

/**
 * Builds the form fields for a bulk member transfer.
 *
 * @param params - Guide, member, and transfer-reason options.
 * @returns FormBuilder configuration.
 */
export function createBulkMemberTransferRequestFormConfig({
  originatingGuideOptions,
  caseloadMemberOptions,
  receivingGuideOptions,
  memberTransferReasons,
  selectedTransferReason,
  selectedMemberIds,
  addressOptionsByMemberId,
}: BulkMemberTransferRequestFormConfigParams): FormConfig<BulkMemberTransferRequestFormType> {
  const config: FormConfig<BulkMemberTransferRequestFormType> = [
    {
      type: 'label',
      label: 'Which guide are these members transferring from?',
    },
    {
      type: 'input',
      name: 'originatingGuideId',
      label: 'From guide',
      input: 'select',
      props: {
        options: originatingGuideOptions,
        required: true,
        fullWidth: true,
        trackingName: 'bulk-transfer-originating-guide-select',
      },
    },
    {
      type: 'input',
      name: 'memberIds',
      input: 'autocomplete',
      label: 'Members',
      props: {
        options: caseloadMemberOptions,
        multiple: true,
        placeholder: 'Type or select members',
        getOptionLabel: (option: FilterOptionDto) => option.label,
        isOptionEqualToValue: (
          option: FilterOptionDto,
          value: FilterOptionDto
        ) => option.value === value.value,
        trackingName: 'bulk-transfer-member-autocomplete',
      },
    },
    {
      type: 'label',
      label: 'What is the reason for the transfer?',
    },
    {
      type: 'input',
      name: 'transferReasonId',
      input: 'radioGroup',
      label: '',
      props: {
        options: memberTransferReasons.map(reason => ({
          label: reason.text,
          value: reason.id,
        })),
        trackingName: 'bulk-transfer-reason-radio-group',
      },
    },
  ];

  if (selectedTransferReason?.detailType === 'date') {
    config.push({
      type: 'input',
      name: 'timestampDetail',
      label: selectedTransferReason.detailLabel ?? 'Effective date',
      input: 'date',
      trackingName: 'bulk-transfer-timestamp-detail-input',
    });
  }
  if (selectedTransferReason?.detailType === 'string') {
    config.push({
      type: 'input',
      name: 'stringDetail',
      label: selectedTransferReason.detailLabel ?? 'Details',
      input: 'text',
      props: { trackingName: 'bulk-transfer-string-detail-input' },
    });
  }
  if (selectedTransferReason?.detailType === 'address') {
    for (const memberId of selectedMemberIds) {
      const memberAddressOptions = addressOptionsByMemberId[memberId];
      config.push({
        type: 'input',
        name: `numericDetailByMemberId.${memberId}` as `numericDetailByMemberId.${string}`,
        label: `${memberAddressOptions?.memberName ?? 'Member'}: ${
          selectedTransferReason.detailLabel ?? 'Select an address'
        }`,
        input: 'select',
        props: {
          options: memberAddressOptions?.options ?? [],
          required: true,
          fullWidth: true,
          trackingName: 'bulk-transfer-address-select',
        },
      });
    }
  }
  if (selectedTransferReason) {
    config.push(
      {
        type: 'label',
        label: 'Which guide should receive these members?',
      },
      {
        type: 'input',
        name: 'receivingGuideId',
        label: 'Select a Guide',
        input: 'select',
        props: {
          options: receivingGuideOptions,
          required: true,
          fullWidth: true,
          trackingName: 'bulk-transfer-receiving-guide-select',
        },
      },
      {
        type: 'input',
        name: 'commentToReceivingGuide',
        label: 'Comments',
        input: 'text',
        props: {
          required: true,
          multiline: true,
          characterLimit: 500,
          trackingName: 'bulk-transfer-comment-input',
        },
      }
    );
  }

  return config;
}
